/**
 * Resolve variables in a configuration object using one or two data sources.
 * @param {Object} config - Object with the templates {key} or {[form].${key}}
 * @param {Object} source1 - Primary data source (Priority).
 * @param {Object} [source2={}] - Secondary data source (Optional).
 * @param {string} [prefix] - Prefijo opcional (ej. "product").
 */

import { isNotEmptyObject } from "../utils/jsonValidator";
import { getNestedValue } from "../utils/objectHandlers";

const replaceAll = (str, search, replacement) => str.split(search).join(replacement);

const toVariablesString = (variables) => {
    if (variables == null) return '';
    if (typeof variables === 'string') return variables;
    try {
        return JSON.stringify(variables);
    } catch {
        return '';
    }
};

const lookupValue = (src, cleanKey, prefix = null) => {
    if (!isNotEmptyObject(src)) return undefined;

    if (prefix) {
        const unprefixed = cleanKey.replace(`${prefix}_`, '');
        if (Object.prototype.hasOwnProperty.call(src, unprefixed)) return src[unprefixed];
        const nestedPrefixed = getNestedValue(src, unprefixed);
        if (nestedPrefixed !== undefined) return nestedPrefixed;
        return undefined;
    }

    if (Object.prototype.hasOwnProperty.call(src, cleanKey)) return src[cleanKey];
    return getNestedValue(src, cleanKey);
};

export function processVariablesDeep(item, values = {}) {
    if (Array.isArray(item)) return item.map((entry) => processVariablesDeep(entry, values));
    if (item && typeof item === 'object') {
        const copy = {};
        for (const key in item) {
            if (key === 'variables') {
                copy[key] = replaceVariablesString(item[key], values);
            } else {
                copy[key] = processVariablesDeep(item[key], values);
            }
        }
        return copy;
    }
    return item;
}

export function replaceVariablesString(variables, values = {}) {
    let result = toVariablesString(variables);
    if (!result) return variables;

    for (const key in values) {
        if (values[key] != null && typeof values[key] === 'object') continue;

        let value = String(values[key] ?? '')
            .replace(/"/g, '\\"')
            .replace(/[\r\n]+/g, ' ');

        if (value === `{${key}}`) value = '';

        result = replaceAll(result, `{${key}}`, value);
        result = replaceAll(result, `{[form].${key}}`, value);
    }
    return result;
}

export function resolveConfig(config, source1, source2 = {}, prefix = null) {

    const getValue = (rawKey) => {
        const cleanKey = rawKey.replace(/[${}[\]]|form\./g, '').trim();

        const sources = [source1, source2];
        for (const src of sources) {
            const found = lookupValue(src, cleanKey, prefix);
            if (found !== undefined) return found;
        }

        return `{${rawKey}}`;
    };

    const process = (item) => {
        if (typeof item === 'string') {
            const exactPlaceholder = item.match(/^\{([^}]+)\}$/);
            if (exactPlaceholder) return getValue(exactPlaceholder[1]);
            return item.replace(/\{([^}]+)\}/g, (_, key) => {
                const value = getValue(key);
                if (value == null || typeof value === 'object') return `{${key}}`;
                return String(value);
            });
        }
        if (Array.isArray(item)) return item.map(process);
        if (typeof item === 'object' && item !== null) {
            const copy = {};
            for (const key in item) copy[key] = process(item[key]);
            return copy;
        }
        return item;
    };

    return process(config);
};

/**
 * Clone and resolve {placeholders} from row data (primary) and company/context (secondary).
 * Never mutates the original template.
 */
export function resolveActionConfig(config, primarySource = {}, secondarySource = {}, prefix = null) {
    const resolved = resolveConfig(config, primarySource, secondarySource, prefix);
    return processVariablesDeep(resolved, { ...secondarySource, ...primarySource });
}

export function serializeSchemeValues(values = {}, scheme = []) {
    if (!values || !Array.isArray(scheme) || scheme.length === 0) return values;
    const next = { ...values };
    scheme.forEach((field) => {
        if (field?.type === 'multiple_select' && Array.isArray(next[field.id])) {
            next[field.id] = next[field.id].filter((entry) => entry != null && entry !== '').join(',');
        }
    });
    return next;
}
